import React, { useState, useRef, useEffect } from "react";
import FileIcon from "./FileIcon";
import { HiOutlineDotsVertical, HiOutlineShare, HiDownload, HiOutlineTrash, HiX, HiCheck } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { requestDownloadAPI, getDownloadUrl, viewFileAPI } from "../services/driveService";
import { server } from "../server";
import { useActivity } from "../ActivityContext";
import { useMemo } from "react";
import { toast } from "react-toastify";
import useAuth from "../useAuth";

const FileCard = React.forwardRef(({ file, index = 0, onView, view = "grid", onRename, onShare, onDelete, isSelected, onToggleSelect, onDownloadClick, isBig }, ref) => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const abortControllerRef = useRef(null);
  const { refreshActivityReport } = useActivity();

  const extension = useMemo(() => {
    return (file.extension || file.name?.split('.').pop() || "").toLowerCase();
  }, [file.extension, file.name]);

  useEffect(() => {
    let isMounted = true;
    
    if (file.thumbnailUrl) {
      const baseUrl = server.replace('/api/v1', '');
      const fullThumbUrl = `${baseUrl}${file.thumbnailUrl}`;
      setBlobUrl(fullThumbUrl);
      setImageError(false);
      return;
    }

    const thumbExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"];
    const isPreviewable = thumbExtensions.includes(extension);

    if (!isPreviewable) {
      setImageError(true);
      return;
    }

    const fetchThumbnail = async () => {
      try {
        const token = localStorage.getItem("dbToken");
        const response = await axios.get(viewFileAPI(file.id), {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });

        if (isMounted) {
          const url = URL.createObjectURL(response.data);
          setBlobUrl(url);
          setImageError(false);
        }
      } catch (err) {
        if (isMounted) setImageError(true);
      }
    };

    fetchThumbnail();

    return () => {
      isMounted = false;
      // Only revoke if it's a blob URL we created, not the direct thumbnailUrl string
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [file.id, file.thumbnailUrl, extension]);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // const handleDownload = async (e) => {
  //   if (e && e.stopPropagation) e.stopPropagation();
  //   abortControllerRef.current = new AbortController();
  //   setDownloading(true);

  //   try {
  //     const token = localStorage.getItem("dbToken");
  //     const requestData = await requestDownloadAPI(file.id);
  //     const tempGuid = requestData?.tempFileGuid || requestData?.data?.tempFileGuid;

  //     if (!tempGuid) throw new Error("Could not acquire download token from server.");

  //     // Refresh activity report as download is initiated
  //     refreshActivityReport();

  //     const url = getDownloadUrl(tempGuid);
  //     const response = await axios.get(url, {
  //       responseType: "blob",
  //       signal: abortControllerRef.current.signal,
  //       headers: { Authorization: `Bearer ${token}` },
  //       onDownloadProgress: (progressEvent) => {
  //         if (progressEvent.total) {
  //           setDownloadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
  //         }
  //       },
  //     });

  //     const blob = new Blob([response.data], { type: response.headers["content-type"] });
  //     const urlObject = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = urlObject;
  //     link.download = file?.name || "download";
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //     setTimeout(() => window.URL.revokeObjectURL(urlObject), 1000);
  //   } catch (err) {
  //     if (!axios.isCancel(err) && err.name !== 'CanceledError') {
  //       console.error("Download failed:", err);
  //     }
  //   } finally {
  //     setDownloading(false);
  //     setDownloadProgress(0);
  //     abortControllerRef.current = null;
  //   }
  // };


  // const handleDownload = async (e) => {
  //   if (e && e.stopPropagation) e.stopPropagation();

  //   try {
  //     const token = localStorage.getItem("dbToken");

  //     const requestData = await requestDownloadAPI(file.id);
  //     const tempGuid =
  //       requestData?.tempFileGuid || requestData?.data?.tempFileGuid;

  //     if (!tempGuid) {
  //       throw new Error("Could not acquire download token from server.");
  //     }

  //     // Refresh activity report
  //     refreshActivityReport();

  //     const url = getDownloadUrl(tempGuid);

  //     // Create direct download link
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = file?.name || "download";

  //     // Pass token if needed via query (ONLY if your backend supports it)
  //     // link.href = `${url}?token=${token}`;

  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (err) {
  //     console.error("Download failed:", err);
  //   }
  // };

  const handleDownload = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    abortControllerRef.current = new AbortController();
    setDownloading(true);

    try {
      const token = localStorage.getItem("dbToken");
      const requestData = await requestDownloadAPI(file.id);
      const tempGuid = requestData?.tempFileGuid || requestData?.data?.tempFileGuid;

      if (!tempGuid) throw new Error("Could not acquire download token from server.");

      // Refresh activity report as download is initiated
      // refreshActivityReport();

      const url = getDownloadUrl(tempGuid);

      // ✅ Use token if backend supports query auth
      const finalUrl = `${url}?token=${token}`;

      const link = document.createElement("a");
      link.href = finalUrl;
      link.download = file?.name || "download";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      if (!axios.isCancel(err) && err.name !== 'CanceledError') {
        console.error("Download failed:", err);
        const errorMsg = typeof err === 'string' ? err : (err?.message || JSON.stringify(err));
        toast.error(`Download failed (F-ID: ${file.id}, U-ID: ${user?.id || 'null'}): ${errorMsg}`);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      abortControllerRef.current = null;
    }
  };


  useEffect(() => {
    const handleTrigger = (e) => {
      if (e.detail.fileId === file.id && !downloading) {
        handleDownload();
      }
    };
    window.addEventListener('triggerDownload', handleTrigger);
    return () => window.removeEventListener('triggerDownload', handleTrigger);
  }, [file.id, downloading]);

  const handleCancelDownload = (e) => {
    e.stopPropagation();
    if (abortControllerRef.current) abortControllerRef.current.abort();
  };

  if (view === "table") {
    return (
      <tr
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        className="group bg-white hover:bg-slate-50 transition-all border-b border-slate-100 last:border-none cursor-pointer"
      >
        <td className="px-6 py-4">
          <div
            onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
            className={`w-5 h-5 rounded-md border-2 transition-all cursor-pointer flex items-center justify-center ${isSelected ? "bg-[#3B82F6] border-[#3B82F6] scale-110" : "border-slate-200 group-hover:border-[#3B82F6] scale-100"
              }`}
          >
            <AnimatePresence>
              {isSelected && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, rotate: -45 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  exit={{ scale: 0, opacity: 0, rotate: 45 }}
                  transition={{ type: "spring", stiffness: 500, damping: 25 }}
                >
                  <HiCheck size={12} className="text-white" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </td>
        <td className="px-6 py-4 text-slate-900">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-slate-50 text-slate-400 group-hover:text-indigo-600 transition-all border border-slate-200 relative">
              <AnimatePresence mode="wait">
                {!imageError && blobUrl ? (
                  <motion.img
                    key="thumb-table"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.2 }}
                    src={blobUrl}
                    alt={file.name}
                    className="w-full h-full object-contain p-0.5"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <motion.div
                    key="icon-table"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.2 }}
                  >
                    <FileIcon extension={extension} className="w-4 h-4" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className=" text-sm truncate max-w-[200px]">{file.name}</span>
          </div>
        </td>
        <td className="hidden md:table-cell px-6 py-4">
          <div className="flex items-center gap-2">
            <img
              src={`https://ui-avatars.com/api/?name=${file.ownerName || "Devon Lane"}&background=random`}
              alt="Avatar"
              className="w-6 h-6 rounded-full border border-white/10"
            />
            <span className="text-sm  text-slate-400">{file.ownerName || "---"}</span>
          </div>
        </td>
        <td className="hidden md:table-cell px-6 py-4">
          <span className="text-sm text-slate-500">{formatFileSize(file.size)}</span>
        </td>
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
            <button onClick={(e) => { e.stopPropagation(); onDownloadClick ? onDownloadClick(file) : handleDownload(e); }} className="p-2.5 rounded-xl bg-slate-50 text-slate-500 hover:text-slate-900 hover:bg-white border border-slate-200 shadow-sm transition-all"><HiDownload size={18} /></button>
            {onShare && <button onClick={(e) => { e.stopPropagation(); onShare?.(file); }} className="p-2.5 rounded-xl bg-slate-50 text-slate-500 hover:text-slate-900 hover:bg-white border border-slate-200 shadow-sm transition-all"><HiOutlineShare size={18} /></button>}
            {onRename && <button onClick={(e) => { e.stopPropagation(); onRename?.(file); }} className="p-2.5 rounded-xl bg-slate-50 text-slate-500 hover:text-slate-900 hover:bg-white border border-slate-200 shadow-sm transition-all"><HiOutlineDotsVertical size={18} /></button>}
            {onDelete && <button onClick={(e) => { e.stopPropagation(); onDelete?.(file); }} className="p-2.5 rounded-xl bg-slate-50 text-slate-500 hover:text-[#F43F5E] hover:bg-white border border-slate-200 shadow-sm transition-all"><HiOutlineTrash size={18} /></button>}
          </div>
        </td>
      </tr>
    );
  }

  if (view === "list") {
    const fileTypeLabel = file.extension ? `${file.extension.toUpperCase()} File` : "File";
    return (
      <motion.div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        className={`flex items-center px-6 py-1.5 hover:bg-slate-100/50 transition-all duration-200 cursor-pointer group border-b border-slate-100 last:border-none relative overflow-hidden ${isSelected ? "bg-indigo-50/50" : ""}`}
      >
        <div
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          className={`mr-4 w-4 h-4 rounded border transition-all flex items-center justify-center shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 scale-110" : "border-slate-300 opacity-0 group-hover:opacity-100 scale-100"
            }`}
        >
          <AnimatePresence>
            {isSelected && (
              <motion.div
                initial={{ scale: 0, opacity: 0, rotate: -45 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0, opacity: 0, rotate: 45 }}
                transition={{ type: "spring", stiffness: 500, damping: 25 }}
              >
                <HiCheck size={10} className="text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex-1 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-slate-100 text-slate-400 group-hover:text-indigo-600 transition-all border border-slate-200/50 relative">
            <AnimatePresence mode="wait">
              {!imageError && blobUrl ? (
                <motion.img
                  key="thumb"
                  initial={{ opacity: 0, scale: 0.5, filter: "blur(4px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 1.2, filter: "blur(4px)" }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  src={blobUrl}
                  alt={file.name}
                  className="w-full h-full object-contain p-0.5"
                  onError={() => setImageError(true)}
                />
              ) : (
                <motion.div
                  key="icon"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <FileIcon extension={extension} className="w-5 h-5" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className=" text-xs text-slate-800 truncate">
            {file.name.length > 30 ? file.name.substring(0, 30) + "..." : file.name}
          </span>
        </div>





        <div className="w-24 text-right text-[11px]  text-slate-400">
          {formatFileSize(file.size)}
        </div>

        <div className="w-32 text-right text-[11px]  text-slate-400">
          {file.createdAt ? new Date(file.createdAt).toLocaleString("en-In") : "-"}
        </div>



        <div className="ml-4 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <button onClick={(e) => { e.stopPropagation(); onDownloadClick ? onDownloadClick(file) : handleDownload(e); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-slate-900 transition-all"><HiDownload size={14} /></button>
          {onShare && <button onClick={(e) => { e.stopPropagation(); onShare?.(file); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-slate-900 transition-all"><HiOutlineShare size={14} /></button>}
          {onRename && <button onClick={(e) => { e.stopPropagation(); onRename?.(file); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-slate-900 transition-all"><HiOutlineDotsVertical size={14} /></button>}
          {onDelete && <button onClick={(e) => { e.stopPropagation(); onDelete?.(file); }} className="p-1 px-1.5 rounded-md hover:bg-white hover:shadow-sm text-slate-400 hover:text-[#F43F5E] transition-all"><HiOutlineTrash size={14} /></button>}
        </div>

        <AnimatePresence>
          {downloading && (
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              className="absolute inset-0 bg-white/95 backdrop-blur-xl z-50 flex items-center px-6 gap-6"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 relative">
                  <HiDownload size={20} className="animate-bounce" />
                  <svg className="absolute inset-0 w-full h-full -rotate-90">
                    <motion.circle cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth={2.5} fill="transparent" initial={{ pathLength: 0 }} animate={{ pathLength: downloadProgress / 100 }} strokeLinecap="round" className="text-indigo-600" />
                  </svg>
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex justify-between items-end mb-1.5">
                    <span className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Downloading {file.name}</span>
                    <span className="font-outfit font-black text-indigo-600 text-lg leading-none">{downloadProgress}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${downloadProgress}%` }} className="h-full bg-indigo-600" />
                  </div>
                </div>
              </div>
              <button
                onClick={handleCancelDownload}
                className="p-3 bg-[#F43F5E]/10 text-[#F43F5E] rounded-xl hover:bg-[#F43F5E]/20 transition-all border border-[#F43F5E]/10"
                title="Cancel Download"
              >
                <HiX size={18} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      whileHover={{ y: -1 }}
      onClick={(e) => e.stopPropagation()}
      className={`group cursor-pointer flex flex-col items-center gap-0.5 p-1 hover:bg-slate-100/50 rounded-xl transition-all duration-300 relative hover:z-50 ${isSelected ? "bg-indigo-50/80 ring-2 ring-indigo-500/20" : ""}`}
    >
      {/* Checkbox for Grid View */}
      <div
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`absolute top-2 left-2 z-[60] w-5 h-5 rounded-lg border-2 transition-all flex items-center justify-center ${isSelected ? "bg-indigo-600 border-indigo-600 scale-110 shadow-lg shadow-indigo-200" : "bg-white border-slate-200 opacity-0 group-hover:opacity-100 scale-90"
          }`}
      >
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scale: 0, opacity: 0, rotate: -45 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 0, opacity: 0, rotate: 45 }}
              transition={{ type: "spring", stiffness: 500, damping: 25 }}
            >
              <HiCheck size={12} className="text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {downloading && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-white/95 backdrop-blur-xl z-50 flex flex-col items-center justify-center transition-all p-2"
          >
            <div className={`bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center text-indigo-600 mb-2 relative overflow-visible ${isBig ? "w-16 h-16" : "w-10 h-10"}`}>
              <HiDownload size={isBig ? 24 : 16} className="animate-bounce" />
              <svg className="absolute inset-0 w-full h-full -rotate-90 overflow-visible">
                <motion.circle cx="50%" cy="50%" r="44%" stroke="currentColor" strokeWidth={2} fill="transparent" initial={{ pathLength: 0 }} animate={{ pathLength: downloadProgress / 100 }} strokeLinecap="round" className="text-indigo-600" />
              </svg>
            </div>
            <div className="flex flex-col items-center">
              <span className="font-outfit font-black text-slate-900 text-sm">{downloadProgress}%</span>
              <button
                onClick={handleCancelDownload}
                className="mt-2 text-[10px] font-bold text-rose-500 hover:text-rose-700 transition-all uppercase tracking-widest flex items-center gap-1"
              >
                <HiX size={12} /> Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full flex items-center justify-center relative transition-all duration-300 bg-slate-50 rounded-2xl overflow-hidden border border-slate-100/50 h-20">
        <AnimatePresence mode="wait">
          {!imageError && blobUrl ? (
            <motion.div
              key="thumb-container"
              className="w-full h-full flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
            >
              <img
                src={blobUrl}
                alt={file.name}
                className="w-full h-full object-contain p-1 transition-all duration-500 group-hover:scale-105"
                onError={() => setImageError(true)}
              />
            </motion.div>
          ) : (
            <motion.div
              key="icon-container"
              className="w-full h-full flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 1.2, rotate: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <FileIcon extension={extension} className="w-10 h-10 drop-shadow-sm group-hover:scale-105 transition-all duration-500" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Shimmer Effect on Load */}
        {!imageError && !blobUrl && (
          <motion.div
            animate={{ x: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent z-10"
          />
        )}
      </div>

      {/* Quick Actions Hover Overlay - 2x2 Grid on Mobile, Vertical on Desktop */}
      <div className="absolute top-1 right-1 grid grid-cols-2 md:flex md:flex-col items-center opacity-0 group-hover:opacity-100 transition-all duration-300 z-50 gap-1 md:gap-0.5">
        <button onClick={(e) => { e.stopPropagation(); onDownloadClick ? onDownloadClick(file) : handleDownload(e); }} className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-slate-600 hover:text-indigo-600 transition-all border border-slate-100 hover:scale-110 active:scale-95" title="Download"><HiDownload size={12} /></button>
        {onShare && <button onClick={(e) => { e.stopPropagation(); onShare?.(file); }} className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-slate-600 hover:text-indigo-600 transition-all border border-slate-100 hover:scale-110 active:scale-95" title="Share"><HiOutlineShare size={12} /></button>}
        {onRename && <button onClick={(e) => { e.stopPropagation(); onRename?.(file); }} className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-slate-600 hover:text-slate-900 transition-all border border-slate-100 hover:scale-110 active:scale-95" title="Rename"><HiOutlineDotsVertical size={12} /></button>}
        {onDelete && <button onClick={(e) => { e.stopPropagation(); onDelete?.(file); }} className="w-6 h-6 md:w-5 md:h-5 bg-white shadow-xl rounded-lg flex items-center justify-center text-rose-500 hover:bg-rose-50 transition-all border border-rose-100 hover:scale-110 active:scale-95" title="Delete"><HiOutlineTrash size={12} /></button>}
      </div>

      <div className="flex flex-col items-center text-center w-full px-0.5 mt-0.5 relative">
        <span className="text-slate-900 block w-full text-[12px] tracking-tight truncate px-1">
          {file.displayName}
        </span>

        {/* Windows-style Metadata Tooltip - Compact & Refined */}
        <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-36 bg-white/95 backdrop-blur-md border border-slate-200 shadow-2xl rounded-lg p-1.5 z-[110] pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-200 text-left scale-95 group-hover:scale-100 origin-top">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-1 mb-0.5">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">File Metadata</span>
            </div>

            <div className="flex flex-col leading-tight">
              <span className="text-[7.5px] font-medium text-slate-400 uppercase tracking-tighter">Size</span>
              <span className="text-[9px] font-semibold text-slate-700">{formatFileSize(file.size)}</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[7.5px] font-medium text-slate-400 uppercase tracking-tighter">Full Name</span>
              <span className="text-[9px] font-semibold text-slate-700 break-words leading-tight">{file.displayName || file.name}</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[7.5px] font-medium text-slate-400 uppercase tracking-tighter">Date Created</span>
              <span className="text-[9px] font-semibold text-slate-700">{file.createdAt ? new Date(file.createdAt).toLocaleString("en-In", { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}</span>
            </div>
          </div>
          {/* Tooltip Arrow */}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-t border-l border-slate-200 rotate-45" />
        </div>
      </div>
    </motion.div>
  );
});


export default FileCard;
